import React, { useState, useEffect } from 'react';
import { Phone, MessageCircle, Navigation, Package, CheckCircle, X, Loader2, ArrowDown, MapPin, Clock } from 'lucide-react';
import { listenToDelivery, markPickedUp, completeDelivery, cancelDelivery, DELIVERY_STATUS } from './deliveryService';
import { listenToCourierLocation } from './driverLocationService';
import { getDistanceFromLatLonInKm } from './utils';

const STATUS_FLOW = [
  { key: DELIVERY_STATUS.ACCEPTED, label: 'שליח בדרך לאיסוף' },
  { key: DELIVERY_STATUS.PICKED_UP, label: 'החבילה נאספה, בדרך למסירה' },
  { key: DELIVERY_STATUS.DELIVERED, label: 'נמסר בהצלחה' },
];

// הערכת זמן הגעה גסה לפי מרחק אווירי (ממוצע ~30 קמ"ש בעיר)
function estimateEta(distanceKm) {
  if (distanceKm == null) return null;
  const minutes = Math.max(1, Math.round((distanceKm / 30) * 60));
  return minutes;
}

/**
 * @param role 'courier' | 'sender'
 */
export default function ActiveDelivery({ deliveryId, role, onClose, onCourierLocationUpdate }) {
  const [delivery, setDelivery] = useState(null);
  const [busy, setBusy] = useState(false);
  const [courierLoc, setCourierLoc] = useState(null); // מיקום חי לחישוב ETA אצל השולח

  useEffect(() => {
    if (!deliveryId) return;
    const unsub = listenToDelivery(deliveryId, setDelivery);
    return () => unsub();
  }, [deliveryId]);

  // השולח מאזין למיקום החי של השליח — גם למפה (להורה) וגם ל-ETA המקומי
  useEffect(() => {
    if (role !== 'sender' || !delivery?.courierId) return;
    const unsub = listenToCourierLocation(delivery.courierId, (loc) => {
      setCourierLoc(loc);
      if (onCourierLocationUpdate) onCourierLocationUpdate(loc);
    });
    return () => unsub();
  }, [role, delivery?.courierId, onCourierLocationUpdate]);

  if (!delivery) {
    return (
      <div className="h-full flex items-center justify-center bg-white">
        <Loader2 className="animate-spin text-gray-400" />
      </div>
    );
  }

  const openWaze = (point) => {
    if (point?.lat) window.open(`https://waze.com/ul?ll=${point.lat},${point.lng}&navigate=yes`, '_blank');
  };
  const openWhatsApp = (phone) => {
    if (phone) window.open(`https://wa.me/${phone.replace(/\D/g, '').replace(/^0/, '972')}`, '_blank');
  };

  const currentStepIndex = STATUS_FLOW.findIndex((s) => s.key === delivery.status);

  const other = role === 'courier'
    ? { name: delivery.senderName, phone: delivery.senderPhone, image: delivery.senderImage, label: 'השולח' }
    : { name: delivery.courierName, phone: delivery.courierPhone, image: null, label: 'השליח' };

  // --- חישוב מצב חי לשולח ---
  // יעד נוכחי של השליח: לפני איסוף → נקודת האיסוף; אחרי איסוף → נקודת המסירה
  let liveDistanceKm = null;
  let liveEta = null;
  let liveTargetLabel = '';
  if (role === 'sender' && courierLoc) {
    const target = delivery.status === DELIVERY_STATUS.PICKED_UP ? delivery.dropoff : delivery.pickup;
    liveTargetLabel = delivery.status === DELIVERY_STATUS.PICKED_UP ? 'ליעד המסירה' : 'לנקודת האיסוף';
    if (target?.lat) {
      liveDistanceKm = getDistanceFromLatLonInKm(courierLoc.lat, courierLoc.lng, target.lat, target.lng);
      liveEta = estimateEta(liveDistanceKm);
    }
  }

  const handlePickedUp = async () => { setBusy(true); await markPickedUp(deliveryId); setBusy(false); };
  const handleComplete = async () => { setBusy(true); await completeDelivery(deliveryId, delivery.fare); setBusy(false); onClose(); };
  const handleCancel = async () => {
    if (!window.confirm('לבטל את המשלוח?')) return;
    setBusy(true);
    await cancelDelivery(deliveryId, role);
    setBusy(false);
    onClose();
  };

  return (
    <div className="h-full flex flex-col bg-white text-right" dir="rtl">

      <div className="p-4 text-white flex justify-between items-center shadow-md bg-blue-600">
        <h2 className="font-bold text-lg flex items-center gap-2"><Package size={20} /> משלוח פעיל</h2>
        <button onClick={onClose} className="bg-white/20 p-2 rounded-full"><X size={20} /></button>
      </div>

      <div className="flex-1 overflow-y-auto p-5 space-y-5 pb-32">

        {/* --- כרטיס מצב חי לשולח (בולט בראש) --- */}
        {role === 'sender' && (delivery.status === DELIVERY_STATUS.ACCEPTED || delivery.status === DELIVERY_STATUS.PICKED_UP) && (
          <div className={`rounded-2xl p-4 text-white shadow-lg ${delivery.status === DELIVERY_STATUS.PICKED_UP ? 'bg-green-600' : 'bg-blue-600'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs opacity-80">
                  {delivery.status === DELIVERY_STATUS.PICKED_UP ? '📦 החבילה נאספה ובדרך!' : '🚗 השליח בדרך לאיסוף'}
                </p>
                {liveEta != null ? (
                  <p className="text-3xl font-black mt-1">~{liveEta} <span className="text-lg font-normal">דק'</span></p>
                ) : (
                  <p className="text-lg font-bold mt-1 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> מאתר מיקום שליח...</p>
                )}
              </div>
              {liveDistanceKm != null && (
                <div className="text-left">
                  <div className="text-2xl font-black">{liveDistanceKm.toFixed(1)}</div>
                  <div className="text-xs opacity-80">ק"מ {liveTargetLabel}</div>
                </div>
              )}
            </div>
            {courierLoc?.updatedAt && (
              <p className="text-[10px] opacity-70 mt-2 flex items-center gap-1">
                <MapPin size={10} /> מתעדכן בזמן אמת
              </p>
            )}
          </div>
        )}

        {/* פס התקדמות */}
        <div className="space-y-3">
          {STATUS_FLOW.map((step, i) => {
            const done = i <= currentStepIndex;
            const active = i === currentStepIndex;
            return (
              <div key={step.key} className="flex items-center gap-3">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                  done ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-400'
                } ${active ? 'ring-4 ring-blue-100' : ''}`}>
                  {done ? <CheckCircle size={16} /> : i + 1}
                </div>
                <span className={`text-sm ${active ? 'font-bold text-blue-700' : done ? 'text-gray-600' : 'text-gray-400'}`}>
                  {step.label}
                </span>
              </div>
            );
          })}
        </div>

        {/* מסלול */}
        <div className="bg-gray-50 p-4 rounded-xl border border-gray-100 space-y-3">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-start gap-2 flex-1">
              <span className="w-2.5 h-2.5 rounded-full bg-green-500 mt-1 shrink-0"></span>
              <div>
                <p className="text-xs text-gray-400">איסוף</p>
                <p className="text-sm text-gray-700">{delivery.pickup?.address}</p>
              </div>
            </div>
            {role === 'courier' && delivery.status === DELIVERY_STATUS.ACCEPTED && (
              <button onClick={() => openWaze(delivery.pickup)} className="bg-blue-600 text-white p-2 rounded-lg shrink-0"><Navigation size={16} /></button>
            )}
          </div>

          <div className="flex justify-center text-gray-200"><ArrowDown size={14} /></div>

          <div className="flex items-start justify-between gap-2">
            <div className="flex items-start gap-2 flex-1">
              <span className="w-2.5 h-2.5 rounded-sm bg-red-500 mt-1 shrink-0"></span>
              <div>
                <p className="text-xs text-gray-400">מסירה</p>
                <p className="text-sm text-gray-700">{delivery.dropoff?.address}</p>
              </div>
            </div>
            {role === 'courier' && delivery.status === DELIVERY_STATUS.PICKED_UP && (
              <button onClick={() => openWaze(delivery.dropoff)} className="bg-blue-600 text-white p-2 rounded-lg shrink-0"><Navigation size={16} /></button>
            )}
          </div>
        </div>

        {/* פרטי החבילה */}
        <div className="bg-white border border-gray-100 rounded-xl p-4">
          <p className="text-xs text-gray-400 mb-1">תוכן החבילה</p>
          <p className="text-sm font-medium text-gray-800">{delivery.parcel?.description}</p>
          {delivery.parcel?.recipientName && (
            <p className="text-xs text-gray-500 mt-2">מקבל: {delivery.parcel.recipientName} · {delivery.parcel.recipientPhone}</p>
          )}
        </div>

        {/* מחיר */}
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 flex items-center justify-between">
          <span className="text-sm text-blue-600">{role === 'courier' ? 'תקבל במזומן' : 'לתשלום במזומן'}</span>
          <span className="text-2xl font-black text-blue-700">₪{delivery.fare}</span>
        </div>

        {/* יצירת קשר */}
        {other.phone && (
          <div className="flex items-center gap-3 bg-gray-50 p-3 rounded-xl border border-gray-100">
            <div className="w-10 h-10 rounded-full bg-gray-200 overflow-hidden flex items-center justify-center shrink-0">
              {other.image ? <img src={other.image} className="w-full h-full object-cover" /> : <Package size={18} className="text-gray-400" />}
            </div>
            <div className="flex-1">
              <p className="text-xs text-gray-400">{other.label}</p>
              <p className="font-bold text-sm">{other.name}</p>
            </div>
            <button onClick={() => openWhatsApp(other.phone)} className="bg-green-500 text-white p-2 rounded-full shadow"><MessageCircle size={18} /></button>
            <a href={`tel:${other.phone}`} className="bg-blue-600 text-white p-2 rounded-full shadow"><Phone size={18} /></a>
          </div>
        )}
      </div>

      {/* סרגל פעולות */}
      <div className="p-4 pb-16 border-t bg-gray-50 z-20 relative shadow-[0_-5px_20px_rgba(0,0,0,0.1)] space-y-2">
        {role === 'courier' && delivery.status === DELIVERY_STATUS.ACCEPTED && (
          <button onClick={handlePickedUp} disabled={busy} className="w-full py-4 bg-black text-white rounded-xl font-bold shadow-lg flex items-center justify-center gap-2 active:scale-95">
            {busy ? <Loader2 className="animate-spin" /> : <>אספתי את החבילה <Package size={18} /></>}
          </button>
        )}
        {role === 'courier' && delivery.status === DELIVERY_STATUS.PICKED_UP && (
          <button onClick={handleComplete} disabled={busy} className="w-full py-4 bg-green-600 text-white rounded-xl font-bold shadow-lg flex items-center justify-center gap-2 active:scale-95">
            {busy ? <Loader2 className="animate-spin" /> : <>מסרתי — סיים משלוח <CheckCircle size={18} /></>}
          </button>
        )}
        {role === 'sender' && delivery.status === DELIVERY_STATUS.ACCEPTED && (
          <div className="text-center text-sm text-gray-500 py-2">השליח בדרך לאסוף את החבילה 🚗</div>
        )}
        {role === 'sender' && delivery.status === DELIVERY_STATUS.PICKED_UP && (
          <div className="text-center text-sm text-green-600 font-bold py-2">החבילה בדרך ליעד 📦</div>
        )}

        {delivery.status === DELIVERY_STATUS.ACCEPTED && (
          <button onClick={handleCancel} disabled={busy} className="w-full py-2.5 text-red-500 text-sm font-bold">בטל משלוח</button>
        )}
      </div>
    </div>
  );
}
